/**
 * Formatting executors — format_range, set_borders, auto_fit, merge_cells.
 *
 * Each export is a CoreFn: (ctx, args?) => Promise<unknown> | unknown
 * that queues operations on proxy objects. The outer `executeExcelTool`
 * wrapper calls `context.sync()` after the core function returns.
 *
 * Office.js Range.format reference:
 * https://learn.microsoft.com/en-us/javascript/api/excel/excel.rangeformat
 */

/* global Excel */

import { splitRangeAreas, splitSheetQualifiedAddress } from "../a1";
import { resolveExcelEnum, resolveRange } from "./utils";

// ---------------------------------------------------------------------------
// format_range
// ---------------------------------------------------------------------------

/**
 * Apply formatting to a range — font, fill, number format, alignment.
 *
 * Expected args:
 *   range:                string   — one area, or several comma-separated
 *                                    ("B8:AB10,B12:AB13") formatted alike
 *   sheetName?:           string
 *   bold?:                boolean
 *   italic?:              boolean
 *   underline?:           boolean
 *   fontColor?:           string   — hex e.g. "#FF0000"
 *   fontSize?:            number
 *   fontName?:            string
 *   fillColor?:           string   — hex
 *   numberFormat?:        string
 *   horizontalAlignment?: string
 *   verticalAlignment?:   string
 *   wrapText?:            boolean
 */
export function formatRangeCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;

  const areas = rangeAddr ? splitRangeAreas(rangeAddr) : [];
  if (!rangeAddr || areas.length === 0) {
    throw new Error("format_range requires a `range` argument.");
  }

  // `Worksheet.getRange` rejects a multi-area address, so each area is
  // resolved on its own. A bare area stays on the sheet of the nearest
  // sheet-qualified area before it ("A!A1,B!B1,C1" puts C1 on B), the way
  // the reference reads, rather than falling back to the active sheet.
  let inheritedSheet = sheetName;
  for (const area of areas) {
    const areaSheet = splitSheetQualifiedAddress(area).sheetName;
    const { range } = resolveRange(
      ctx,
      area,
      areaSheet === undefined ? inheritedSheet : sheetName
    );
    applyRangeFormat(range, args);
    inheritedSheet = areaSheet ?? inheritedSheet;
  }

  return {
    success: true,
    range: rangeAddr,
    ...(areas.length > 1 ? { areas: areas.length } : {}),
  };
}

function applyRangeFormat(
  range: Excel.Range,
  args: Record<string, unknown> | undefined
): void {
  const fmt = range.format;

  // Font properties
  if (args?.bold !== undefined) {
    fmt.font.bold = args.bold as boolean;
  }
  if (args?.italic !== undefined) {
    fmt.font.italic = args.italic as boolean;
  }
  if (args?.underline !== undefined) {
    fmt.font.underline = (args.underline as boolean)
      ? Excel.RangeUnderlineStyle.single
      : Excel.RangeUnderlineStyle.none;
  }
  if (args?.fontColor) {
    fmt.font.color = args.fontColor as string;
  }
  if (args?.fontSize) {
    fmt.font.size = args.fontSize as number;
  }
  if (args?.fontName) {
    fmt.font.name = args.fontName as string;
  }

  // Fill
  if (args?.fillColor) {
    fmt.fill.color = args.fillColor as string;
  }
  if (args?.fillPattern) {
    fmt.fill.pattern = resolveExcelEnum(
      Excel.FillPattern,
      args.fillPattern as string,
      "fillPattern"
    );
    if (args?.patternColor) {
      fmt.fill.patternColor = args.patternColor as string;
    }
  }

  // Number format
  if (args?.numberFormat) {
    range.numberFormat = [[args.numberFormat as string]];
  }

  // Alignment
  if (args?.horizontalAlignment) {
    fmt.horizontalAlignment = resolveExcelEnum(
      Excel.HorizontalAlignment,
      args.horizontalAlignment as string,
      "horizontalAlignment"
    );
  }

  if (args?.verticalAlignment) {
    fmt.verticalAlignment = resolveExcelEnum(
      Excel.VerticalAlignment,
      args.verticalAlignment as string,
      "verticalAlignment"
    );
  }

  if (args?.wrapText !== undefined) {
    fmt.wrapText = args.wrapText as boolean;
  }
}

// ---------------------------------------------------------------------------
// set_borders
// ---------------------------------------------------------------------------

/**
 * Apply border styles to a range.
 *
 * Expected args:
 *   range:      string
 *   sheetName?: string
 *   edges?:     string[] — border edge identifiers
 *   style:      string   — border line style
 *   color?:     string   — hex color
 *   weight?:    string   — hairline | thin | medium | thick
 */
export function setBordersCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const style = args?.style as string | undefined;

  if (!rangeAddr) {
    throw new Error("set_borders requires a `range` argument.");
  }
  if (!style) {
    throw new Error("set_borders requires a `style` argument.");
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  const borderStyle = resolveExcelEnum(
    Excel.BorderLineStyle,
    style,
    "border style"
  );
  const borderWeight = resolveExcelEnum(
    Excel.BorderWeight,
    (args?.weight as string) ?? "thin",
    "border weight"
  );
  const borderColor = (args?.color as string) ?? "#000000";

  // Default to all outer edges if not specified
  const edgeNames = (args?.edges as string[] | undefined) ?? [
    "edgeTop",
    "edgeBottom",
    "edgeLeft",
    "edgeRight",
  ];

  for (const edgeName of edgeNames) {
    const borderIndex = resolveExcelEnum(
      Excel.BorderIndex,
      edgeName,
      "border edge"
    );
    const border = range.format.borders.getItem(borderIndex);
    border.style = borderStyle;
    border.weight = borderWeight;
    border.color = borderColor;
  }

  return { success: true, range: rangeAddr, edgesApplied: edgeNames.length };
}

// ---------------------------------------------------------------------------
// auto_fit
// ---------------------------------------------------------------------------

/**
 * Auto-fit column widths and/or row heights for a range.
 *
 * Expected args:
 *   range:       string
 *   sheetName?:  string
 *   fitColumns?: boolean (default true)
 *   fitRows?:    boolean (default true)
 */
export function autoFitCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const fitColumns = args?.fitColumns !== false; // default true
  const fitRows = args?.fitRows !== false; // default true

  if (!rangeAddr) {
    throw new Error("auto_fit requires a `range` argument.");
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  if (fitColumns) {
    range.format.autofitColumns();
  }
  if (fitRows) {
    range.format.autofitRows();
  }

  return {
    success: true,
    range: rangeAddr,
    fittedColumns: fitColumns,
    fittedRows: fitRows,
  };
}

// ---------------------------------------------------------------------------
// set_dimensions
// ---------------------------------------------------------------------------

/**
 * Set an explicit row height and/or column width on a range, in points.
 *
 * Unlike `auto_fit` (which only grows rows/columns to fit content),
 * `set_dimensions` sets the exact size requested — the only way to shrink a
 * row/column.
 *
 * Expected args:
 *   range:        string
 *   sheetName?:   string
 *   rowHeight?:   number — points
 *   columnWidth?: number — points
 */
export function setDimensionsCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const rowHeight = args?.rowHeight as number | undefined;
  const columnWidth = args?.columnWidth as number | undefined;

  if (!rangeAddr) {
    throw new Error("set_dimensions requires a `range` argument.");
  }
  if (rowHeight === undefined && columnWidth === undefined) {
    throw new Error(
      "set_dimensions requires at least one of `rowHeight` or `columnWidth`."
    );
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);
  if (rowHeight !== undefined) {
    range.format.rowHeight = rowHeight;
  }
  if (columnWidth !== undefined) {
    range.format.columnWidth = columnWidth;
  }

  return { success: true, range: rangeAddr, rowHeight, columnWidth };
}

// ---------------------------------------------------------------------------
// merge_cells
// ---------------------------------------------------------------------------

/**
 * Merge or unmerge a range of cells.
 *
 * Expected args:
 *   range:      string
 *   sheetName?: string
 *   action:     "merge" | "unmerge"
 *   across?:    boolean — merge each row separately
 */
export function mergeCellsCore(
  ctx: Excel.RequestContext,
  args?: Record<string, unknown>
): unknown {
  const rangeAddr = args?.range as string | undefined;
  const sheetName = args?.sheetName as string | undefined;
  const action = args?.action as string | undefined;

  if (!rangeAddr) {
    throw new Error("merge_cells requires a `range` argument.");
  }
  if (!action || (action !== "merge" && action !== "unmerge")) {
    throw new Error(
      "merge_cells requires an `action` argument ('merge' or 'unmerge')."
    );
  }

  const { range } = resolveRange(ctx, rangeAddr, sheetName);

  if (action === "merge") {
    const across = args?.across === true;
    range.merge(across);
  } else {
    range.unmerge();
  }

  return { success: true, range: rangeAddr, action };
}
